/**
 * Enhanced screening score calculation with completeness penalties and quality gates
 */

interface ScreeningData {
  transcript?: string;
  ai_summary?: any;
  screening_results?: any;
  duration_seconds?: number;
  session_status?: string;
}

interface ScoreBreakdown {
  totalScore: number;
  breakdown: {
    informationCompleteness: number;
    communicationQuality: number;
    technicalAssessment: number;
    callEngagement: number;
  };
  penalties: string[];
  qualityGates: string[];
}

export const calculateEnhancedScreeningScore = (session: ScreeningData): ScoreBreakdown => {
  const transcript = session.transcript?.toLowerCase() || '';
  const duration = session.duration_seconds || 0;
  const aiSummary = session.ai_summary;
  
  // Start with lower base score (20 instead of 50)
  let totalScore = 20;
  const penalties: string[] = [];
  const qualityGates: string[] = [];
  
  // Information Completeness Score (40% weight - max 40 points)
  let completenessScore = 0;
  
  // Check for candidate name identification (+8 points)
  const hasName = aiSummary?.candidate_summary?.name && 
    aiSummary.candidate_summary.name !== 'Unknown' && 
    aiSummary.candidate_summary.name !== 'Not mentioned';
  if (hasName) {
    completenessScore += 8;
  } else {
    penalties.push('No candidate identification (-20 points)');
    totalScore -= 20;
  }
  
  // Check for technical skills discussion (+8 points)
  const hasSkills = aiSummary?.candidate_summary?.primary_skills?.length > 0 ||
    transcript.includes('experience') || transcript.includes('skill') || transcript.includes('developer');
  if (hasSkills) {
    completenessScore += 8;
  } else {
    penalties.push('No technical skills discussed (-15 points)');
    totalScore -= 15;
  }
  
  // Check for availability discussion (+6 points)
  const hasAvailability = aiSummary?.candidate_summary?.availability !== 'Not discussed' ||
    transcript.includes('available') || transcript.includes('start');
  if (hasAvailability) {
    completenessScore += 6;
  } else {
    penalties.push('No availability mentioned (-10 points)');
    totalScore -= 10;
  }
  
  // Check for salary expectations (+6 points)
  const hasSalary = aiSummary?.candidate_summary?.salary_expectations !== 'Not discussed' ||
    transcript.includes('salary') || transcript.includes('rate') || transcript.includes('compensation');
  if (hasSalary) {
    completenessScore += 6;
  } else {
    penalties.push('No salary expectations discussed (-10 points)');
    totalScore -= 10;
  }
  
  // Check for experience discussion (+6 points)
  const hasExperience = aiSummary?.candidate_summary?.years_experience !== 'Not mentioned' ||
    transcript.includes('years') || transcript.includes('experience');
  if (hasExperience) {
    completenessScore += 6;
  } else {
    penalties.push('No experience details (-8 points)');
    totalScore -= 8;
  }
  
  // Check for current role (+6 points)
  const hasCurrentRole = aiSummary?.candidate_summary?.current_role !== 'Not mentioned' ||
    transcript.includes('currently') || transcript.includes('working');
  if (hasCurrentRole) {
    completenessScore += 6;
  }
  
  // Communication Quality Score (30% weight - max 30 points)
  let communicationScore = 0;
  
  // AI assessment of communication quality
  if (aiSummary?.screening_assessment?.communication_quality) {
    const commQuality = aiSummary.screening_assessment.communication_quality;
    if (commQuality === 'excellent') communicationScore += 30;
    else if (commQuality === 'good') communicationScore += 22;
    else if (commQuality === 'average') communicationScore += 15;
    else if (commQuality === 'poor') communicationScore += 5;
  } else {
    // Fallback analysis based on transcript
    if (transcript.length > 500) communicationScore += 15;
    else if (transcript.length > 200) communicationScore += 10;
    else communicationScore += 5;
  }
  
  // Check for red flags that damage communication score
  if (transcript.includes('skip') || transcript.includes('skip it')) {
    communicationScore -= 15;
    penalties.push('Unwilling to follow process (-15 points)');
  }
  if (transcript.includes('treating me unfairly') || transcript.includes('recommend me now')) {
    communicationScore -= 20;
    penalties.push('Inappropriate/demanding behavior (-20 points)');
  }
  if (transcript.includes('get you in trouble')) {
    communicationScore -= 25;
    penalties.push('Threatening behavior (-25 points)');
  }
  
  // Technical Assessment Score (20% weight - max 20 points)
  let technicalScore = 0;
  
  if (aiSummary?.screening_assessment?.technical_competency) {
    const techComp = aiSummary.screening_assessment.technical_competency;
    if (techComp === 'high') technicalScore += 20;
    else if (techComp === 'medium') technicalScore += 14;
    else if (techComp === 'low') technicalScore += 8;
    else technicalScore += 3;
  } else {
    // Fallback based on keywords
    if (transcript.includes('senior') || transcript.includes('lead')) technicalScore += 12;
    if (transcript.includes('microsoft sql') || transcript.includes('developer')) technicalScore += 8;
  }
  
  // Call Engagement Score (10% weight - max 10 points)
  let engagementScore = 0;
  
  // Duration-based engagement
  if (duration >= 600) engagementScore += 10;      // 10+ minutes = full points
  else if (duration >= 300) engagementScore += 7;  // 5-10 minutes = good
  else if (duration >= 180) engagementScore += 4;  // 3-5 minutes = minimal
  else engagementScore += 1;                        // < 3 minutes = poor
  
  // Apply Quality Gates (hard limits)
  if (duration < 180) {
    qualityGates.push('Call too short (<3 minutes) - max 25 points');
    totalScore = Math.min(totalScore, 25);
  }
  
  if (!hasName) {
    qualityGates.push('No candidate identification - max 35 points');
    totalScore = Math.min(totalScore, 35);
  }
  
  if (transcript.length < 100) {
    qualityGates.push('Minimal conversation - max 40 points');
    totalScore = Math.min(totalScore, 40);
  }
  
  // Add component scores to total
  totalScore += completenessScore + communicationScore + technicalScore + engagementScore;
  
  // Ensure score is within bounds
  totalScore = Math.max(0, Math.min(100, totalScore));
  
  return {
    totalScore,
    breakdown: {
      informationCompleteness: completenessScore,
      communicationQuality: Math.max(0, communicationScore),
      technicalAssessment: technicalScore,
      callEngagement: engagementScore
    },
    penalties,
    qualityGates
  };
};

export const getRecommendationFromScore = (score: number): string => {
  if (score >= 75) return 'Strong Recommendation';
  if (score >= 60) return 'Recommended';
  if (score >= 45) return 'Under Review';
  if (score >= 30) return 'Concerns';
  return 'Not Recommended';
};